//@flow

import React, { Fragment, Component } from 'react';
import isEmpty from 'lodash/isEmpty';
import { connect } from 'react-redux';
import { Modal, ModalBody, ModalHeader, Col, Container, Row } from 'reactstrap';

import {
  Sticky,
  Loading,
  Toggler,
  WhitePanel,
  SemiTransparentLoader,
  CostFooter,
} from '../../../../components';

import Lodges from './Lodges';
import Flights from './Flights';
import BookSection from './BookSection';
import PriceCalculator from './PriceCalculator';
import AdditionalServices from './AdditionalServices';

import { getSelectedTrekById } from '../../../../redux/actions/selectedTrekActions';
import { getTrekPaymentDetails } from '../../../../redux/actions/trekPaymentReviewActions';
import { trackTrekPaymentReviewPageview } from '../../../../redux/actions/analyticsEventActions';
import { appendHoneyGuide } from '../../../../lib/utils';

type Props = {
  trek: {
    name: string,
    number_of_days: number,
  },
  totalLodgePrice: number,
  totalFlightPrice: number,
  totalPrice: number,
  totalAdditionalPrice: number,
  showBuyTrek: boolean,
  isLoading: boolean,
  flightsAvailable: boolean,
  trekHasNoFlights: boolean,
  fetchTrek: () => void,
  fetchTrekReview: () => void,
  trackPageView: () => void,
};

const mapStateToProps = state => {
  if (!state.selectedTrek.trek) return null;

  const {
    trekPayment,
    selectedTrek: { trek },
  } = state;
  const {
    totalLodgePrice,
    totalFlightPrice,
    totalPrice,
    totalAdditionalPrice,
  } = PriceCalculator({ ...trekPayment, numberOfDays: trek.number_of_days });

  const { isFlightsAvailable, userIsPickingDatesOrPax, isLoading, flights } = trekPayment;
  const trekHasNoFlights = isEmpty(flights);

  return {
    trek,
    totalLodgePrice,
    totalFlightPrice,
    totalPrice,
    totalAdditionalPrice,
    isLoading,
    showBuyTrek: trekHasNoFlights ? true : isFlightsAvailable && !userIsPickingDatesOrPax,
    flightsAvailable: trekHasNoFlights
      ? true
      : isFlightsAvailable && !userIsPickingDatesOrPax,
    trekHasNoFlights,
  };
};

const mapDispatchToProps = (dispatch, { trekId }) => ({
  fetchTrek: () => dispatch(getSelectedTrekById(trekId)),
  fetchTrekReview: () => dispatch(getTrekPaymentDetails(trekId)),
  trackPageView: () => dispatch(trackTrekPaymentReviewPageview()),
});

class Review extends Component<Props> {
  closeBookSectionModal: () => void;
  isBookSectionModalOpen: boolean;

  async componentDidMount() {
    await this.fetchTrekData();

    const { trackPageView, trek } = this.props;
    trackPageView();
    if (trek) document.title = appendHoneyGuide(`Trek Review | ${trek.name}`);
  }

  async fetchTrekData() {
    try {
      const { fetchTrek, fetchTrekReview, trek } = this.props;

      if (!trek) await fetchTrek();
      await fetchTrekReview();
    } catch (err) {
      console.log(err);
    }
  }

  componentDidUpdate() {
    // BookSection is in modal in Mobile Devices.
    // Since after successful searching the flights.
    // The modal should be closed.
    if (this.props.flightsAvailable && this.isBookSectionModalOpen)
      this.closeBookSectionModal();
  }

  render() {
    if (!this.props.trek) return <Loading />;

    const {
      totalLodgePrice,
      totalFlightPrice,
      totalPrice,
      totalAdditionalPrice,
      trek: { name, number_of_days },
      showBuyTrek,
      isLoading,
      trekHasNoFlights,
    } = this.props;

    return (
      <Container id="trekReview">
        <div className="review-holder">
          <Row>
            <div className="d-block d-lg-none">
              <Toggler>
                {({ on, open, close, toggle }) => {
                  this.isBookSectionModalOpen = on;
                  this.closeBookSectionModal = close;
                  return (
                    <Fragment>
                      <Modal isOpen={on} toggle={toggle}>
                        <SemiTransparentLoader
                          isLoading={isLoading}
                          loadingText={
                            showBuyTrek ? 'Booking Your Trek' : 'Searching For Flight'
                          }
                        />
                        <ModalHeader toggle={close}>Trek Review</ModalHeader>
                        <ModalBody>
                          <div className="aside">
                            <BookSection totalPrice={totalPrice} callback={close} />
                          </div>
                        </ModalBody>
                      </Modal>
                      <CostFooter
                        currency="USD"
                        amount={totalPrice}
                        onClick={open}
                        buttonText={showBuyTrek ? 'BUY TREK' : 'CHECK FOR AVAILABILITY'}
                      />
                    </Fragment>
                  );
                }}
              </Toggler>
            </div>
            <Col xl="8" lg="7">
              <TrekCard name={name} numberOfDays={number_of_days} />
              <AdditionalServices totalAdditionalPrice={totalAdditionalPrice} />
              <WhitePanel className="trekreview">
                <h2 className="trekreview-title">INCLUDED SERVICES</h2>
                {!trekHasNoFlights && <Flights totalFlightPrice={totalFlightPrice} />}
                <Lodges totalLodgePrice={totalLodgePrice} />
              </WhitePanel>
            </Col>
            <Col xl="4" lg="5" className="d-none d-lg-block">
              <Sticky className="aside">
                <BookSection totalPrice={totalPrice} />
              </Sticky>
            </Col>
          </Row>
        </div>
        <SemiTransparentLoader
          isLoading={isLoading}
          loadingText={showBuyTrek ? 'Booking Your Trek' : 'Searching For Flight'}
        />
      </Container>
    );
  }
}

type TrekCardProps = {
  name: string,
  numberOfDays: number,
};

const TrekCard = ({ name, numberOfDays }: TrekCardProps) => (
  <WhitePanel className="trekreview">
    <h2 className="trekreview-title">
      {name} - {numberOfDays} Days [REVIEW]
    </h2>
    <p>
      <strong>This Trek Includes:</strong>
      <ol className="number-list">
        <li>Flights between Lukla and Kathmandu. (Free Refund before 24 hours)</li>
        <li>
          Lodge Booking with Dinner (Choice of Entree with Soup) and Set Breakfast. (Free
          Refund 7 days before the booking date; Date change of +/- 2 days at no extra cost)
        </li>
        <li>
          Option to Book Guides and Porter. (Free Refund 60 days before the trek start
          date.)
        </li>
      </ol>
    </p>
    <p>
      <strong>Alternatively, you can:</strong>
      <ol className="number-list">
        <li>
          <a href="/flights" target="_blank">
            Book Flights
          </a>{' '}
          Only
        </li>
        <li>Book Individual Lodges (Click on the Individual Lodges Below)</li>
        <li>
          <a href="/guides" target="_blank">
            Book Guides
          </a>
        </li>
      </ol>
    </p>
    <p>
      Or if you have special requests you can{' '}
      <a href="/onboarding-form" target="_blank">
        Contact Us Directly
      </a>
      .
    </p>
  </WhitePanel>
);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Review);



// WEBPACK FOOTER //
// ./src/screens/Trek/Self-Guided/Review/Review.js